import React from 'react';

import { MCButton, MCError, MCText, SelectField, TextField, View } from 'src/components/basic';
import BaseComponent from 'src/components/BaseComponent';

import {
  scssVariables as variables,
  lang,
  remCss,
  objectToSelectList,
  generateGoogleIdToken
} from 'src/helpers';

import { DateSelects, ThumbnailUploader } from 'src/components/complex';
import LANG_CONST from 'src/helpers/lang/const';
import { Puff } from 'react-loader-spinner';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import model from './model';
import moment from 'moment/moment';
import { GoogleButtonContainer } from '../user/GoogleSignup';

class Signup extends BaseComponent {
  type = 'patient';

  componentDidMount() {
    model.subscribe(this.subscription);
    model.resetState();

    const gid_token = this.props.gidToken;
    const payload = gid_token ? generateGoogleIdToken(gid_token) : {};

    model.setState({
      type: this.type,
      usernameParams: this.props.usernameParams || {},
      gid_token,
      firstName: payload?.firstName,
      lastName: payload?.lastName,
      email: payload?.email,
      name: payload?.name,
      avatar: payload?.picture
    });

    this.scrollTo(gid_token ? 1 : 0);
    if (this.props.onStep) this.props.onStep(gid_token ? 1 : 0, false);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  validationSubmit = (e) => {
    e.preventDefault();
    const step = model.getState().step || 0;
    const steps = this.steps();
    const fields = steps[step].fields;

    if (fields.length < 0) {
      return this.scrollTo(step + 1);
    }

    const type = this.type;

    model.verifyFields(type, this.getParams(fields)).then((ok) => {
      if (ok === true) {
        if (steps[step + 1]?.fields?.includes('username')) model.suggestionUsernameByName();
        if (!steps[step + 1]) this.afterSubmit(e);
        else this.scrollTo(step + 1);
      }
    });
  };

  afterSubmit = (e) => {
    e.preventDefault();
    if (this.props.handleSignupSubmit) {
      const fields = [];
      const steps = this.steps();

      steps.forEach((s) => {
        fields.push(...s.fields);
      });

      const params = this.getParams(fields);

      model.verifyFields(this.type, params).then((ok) => {
        console.log('ok phone', ok);
        if (ok === true) {
          this.props.handleSignupSubmit(e, params);
        } else {
          const invalidKeys = Object.keys(model.getApiState('signup').invalid);
          let invalidStep = model.state.step;
          steps.forEach((item, index) => {
            if (item.fields.find((k) => invalidKeys.includes(k))) {
              invalidStep = Math.min(invalidStep, index);
            }
          });
          this.scrollTo(invalidStep);
        }
      });
    } else {
      model.signUp(this.props.authCb, this.props.code);
    }
  };

  getParams = (fields) => {
    const params = {};

    for (let field of fields) {
      params[field] = model.state[field];
      if (field !== 'avatar' && field !== 'birthday') {
        params[field] = params[field]?.toString().trim();
      }
    }

    return params;
  };

  getApiState = () => {
    const createState = model.getApiState('signup');
    createState.invalid = { ...this.props.invalid, ...createState.invalid };
    return createState;
  };

  renderAvatar = () => (
    <View style={{ alignItems: 'center', paddingTop: remCss(5) }}>
      <ThumbnailUploader
        type={'round'}
        image={model.input('avatar')}
        setImage={(data) => model.handleChange('avatar', data)}
        autoLoad={true}
        activeCloseBtn={false}
        style={{ width: 90, height: 90 }}
      />
    </View>
  );

  renderOptions = () => {
    let { loading } = model.getApiState('signup');
    return (
      <>
        <GoogleButtonContainer role={this.type} />
        <MCButton
          large
          primary
          radius
          dark
          disabled={loading}
          onClick={() => {
            model.resetApi();
            this.scrollTo(1);
          }}
          loading={loading}
        >
          <SVGIcon iconname={BASE_ICONS.EMAIL} width={40} fill={'white'} />
          <MCText white weight={500} size={20} style={{ paddingRight: remCss(45) }}>
            {lang.t(LANG_CONST.buttons.signup_with_email)}
          </MCText>
        </MCButton>
      </>
    );
  };

  renderEmail = () => {
    const apiStateId = 'signup';
    const { handleBlur } = model;
    const createState = this.getApiState();
    const { invalid } = createState;

    if (createState.error === 'EMAIL_TAKEN') {
      invalid['email'] = createState.errorMessage;
      createState.error = null;
    }

    return (
      <>
        <TextField
          autoFocus={!this.props.bigForm}
          template={6}
          name={'email'}
          type='email'
          autoComplete='email'
          value={model.input('email')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            model.handleChangeForEmail(n, v);
          }}
          onBlur={handleBlur}
          onFocus={this.onFocus}
          label={LANG_CONST.email_address}
          invalid={invalid}
          suffixEl={
            model.getApiState('checkEmail').loading ? (
              <div className={'mc-plr10 mc-mr5'}>
                <Puff
                  color={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : model.isValid('emailAddress', model.input('email')) && !invalid?.email ? (
              <div className={'mc-plr10 mc-mr5 mc-flex mc-flex-center'}>
                <SVGIcon
                  outline
                  circle
                  padding={remCss(5)}
                  iconname={BASE_ICONS.CHECK}
                  fill={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : (
              ''
            )
          }
        />
      </>
    );
  };

  renderName = () => {
    const apiStateId = 'signup';
    const { handleChange, handleBlur } = model;
    const createState = this.getApiState();
    const { invalid } = createState;

    return (
      <>
        <TextField
          autoFocus={true}
          template={6}
          name={'firstName'}
          autoComplete='given-name'
          value={model.input('firstName')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          onFocus={this.onFocus}
          onBlur={handleBlur}
          label={LANG_CONST.first_name}
          invalid={invalid}
        />
        <TextField
          template={6}
          name={'lastName'}
          autoComplete='family-name'
          value={model.input('lastName')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          onFocus={this.onFocus}
          onBlur={handleBlur}
          label={LANG_CONST.last_name}
          invalid={invalid}
        />
      </>
    );
  };

  renderUsername = () => {
    const { handleChange, handleBlur } = model;
    const createState = this.getApiState();
    const { invalid } = createState;

    if (createState.error === 'URLNAME_TAKEN') {
      invalid['username'] = createState.errorMessage;
      createState.error = null;
    }

    return (
      <>
        <TextField
          autoFocus={!this.props.bigForm}
          onFocus={this.onFocus}
          template={6}
          name={'username'}
          // placeholder={LANG_CONST.username}
          type='text'
          autoComplete='off'
          value={model.input('username')}
          suffixEl={
            model.getApiState('verifyFields').loading ? (
              <div className={'mc-plr10 mc-mr5'}>
                <Puff
                  color={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : model.input('username') && !invalid['username'] ? (
              <div className={'mc-plr10 mc-mr5 mc-flex mc-flex-center'}>
                <SVGIcon
                  outline
                  circle
                  padding={remCss(5)}
                  iconname={BASE_ICONS.CHECK}
                  fill={variables['colors-mc-primary']}
                  height={remCss(25)}
                  width={remCss(25)}
                />
              </div>
            ) : (
              ''
            )
          }
          onChange={(n, v) => model.handleChangeForUsername(n, v)}
          onBlur={handleBlur}
          label={LANG_CONST.username}
          invalid={invalid}
        />
        {model.state.usernameSuggestion && (
          <span className={'mc-label-416'}>
            {LANG_CONST.suggestion}:{' '}
            <span
              className={'mc-primary-416 mc-pointer'}
              onClick={() => {
                handleChange('username', model.state.usernameSuggestion);
              }}
            >
              {model.state.usernameSuggestion}
            </span>
          </span>
        )}
      </>
    );
  };

  renderPw = (hasConfirm = false) => {
    const apiStateId = 'signup';
    const { handleChange, handleBlur } = model;
    const createState = this.getApiState();
    const { invalid } = createState;
    return (
      <>
        <TextField
          autoFocus={!this.props.bigForm}
          template={6}
          type='password'
          name={'password'}
          autoComplete='new-password'
          label={LANG_CONST.password}
          value={model.input('password')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          onFocus={this.onFocus}
          onBlur={handleBlur}
          invalid={invalid}
        />
        <div className='mc-label-420'>{lang.t(LANG_CONST.text.password_least_char)}</div>
        {hasConfirm && (
          <TextField
            template={6}
            type='password'
            name={'password2'}
            autoComplete='new-password'
            label={LANG_CONST.confirm_password}
            value={model.input('password2')}
            onChange={(n, v) => {
              model.resetApiState(apiStateId);
              handleChange(n, v);
            }}
            onBlur={handleBlur}
            onFocus={this.onFocus}
            invalid={invalid}
          />
        )}
      </>
    );
  };

  renderBirthday = () => {
    const apiStateId = 'signup';
    const { handleChange } = model;
    const createState = this.getApiState();
    const { invalid } = createState;
    return (
      <div className={'mc-flex-20 mc-flex-start'}>
        <div className={'mc-flex-col mc-grow'}>
          <div className={'mc-label-418'}>{lang.t('Birthday')}</div>
          <DateSelects
            className={'mc-user-birthday'}
            onFocus={this.onFocus}
            close={false}
            template={'6'}
            minDate={moment().startOf('year').year(1900)}
            maxDate={moment().add({ years: -14 }).endOf('year')}
            onChange={(n, v) => {
              model.resetApiState(apiStateId);
              handleChange(n, v);
            }}
            invalid={invalid}
            name={'birthday'}
            date={model.input('birthday')}
          />

          {invalid['birthday'] && <p className='mc-form-invalid-text'>{invalid['birthday']}</p>}
        </div>
      </div>
    );
  };

  renderGender = () => {
    const apiStateId = 'signup';
    const { handleChange } = model;
    const createState = this.getApiState();
    const { invalid } = createState;
    return (
      <>
        <SelectField
          onFocus={this.onFocus}
          template={6}
          width={'100%'}
          options={objectToSelectList(LANG_CONST.constants.gender)}
          label={LANG_CONST.gender}
          isSearchable={false}
          isClearable={false}
          name={'gender'}
          value={model.input('gender')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          invalid={invalid}
        />
      </>
    );
  };

  renderPhoneNumber = () => {
    const apiStateId = 'signup';
    const { handleChange, handleBlur } = model;
    const createState = this.getApiState();
    const { invalid } = createState;
    return (
      <>
        <TextField
          autoFocus={true}
          onFocus={this.onFocus}
          template={6}
          defaultCountryName={model.state.countryName}
          name={'phoneNumber'}
          type='phone'
          autoComplete='tel'
          value={model.input('phoneNumber')}
          onChange={(n, v) => {
            model.resetApiState(apiStateId);
            handleChange(n, v);
          }}
          onBlur={handleBlur}
          label={LANG_CONST.phone_number}
          invalid={invalid}
        />
      </>
    );
  };

  scrollTo = (step) => {
    model.setState({ step });
  };

  onFocus = () => {
    if (!this.props.bigForm) {
      setTimeout(() => {
        this.submitRef?.scrollIntoView();
      }, 300);
    }
  };

  steps = () => {
    if (!this.props.bigForm) {
      return [
        { fields: [], content: () => <>{this.renderOptions()}</> },
        {
          fields: ['avatar', 'email'],
          content: () => (
            <>
              {this.renderAvatar()}
              {this.renderEmail()}
            </>
          )
        },
        {
          fields: ['firstName', 'lastName'],
          content: () => <>{this.renderName()}</>
        },
        {
          fields: ['username'],
          content: () => <>{this.renderUsername()}</>
        },
        {
          fields: ['password', 'password2'],
          content: () => <>{this.renderPw(true)}</>
        },
        {
          fields: ['birthday', 'gender'],
          content: () => (
            <>
              {this.renderBirthday()}
              {this.renderGender()}
            </>
          )
        },
        {
          fields: ['phoneNumber'],
          content: () => <>{this.renderPhoneNumber()}</>
        }
      ].filter((c) => !!c);
    }

    return [
      { fields: [], content: () => <>{this.renderOptions()}</> },
      {
        fields: ['avatar', 'firstName', 'lastName', 'email', 'password'],
        content: () => (
          <>
            {this.renderAvatar()}
            {this.renderName()}
            {this.renderEmail()}
            {this.renderPw(false)}
          </>
        )
      },
      {
        fields: ['birthday', 'gender', 'username'],
        content: () => (
          <>
            {this.renderBirthday()}
            {this.renderGender()}
            {this.renderUsername()}
          </>
        )
      },
      {
        fields: ['phoneNumber'],
        content: () => <>{this.renderPhoneNumber()}</>
      }
    ].filter((c) => !!c);
  };

  render() {
    const createState = this.getApiState();
    const activeStep = model.getState().step || 0;

    const { invalid } = createState;
    let loading =
      model.getApiState('signup').loading ||
      model.getApiState('verifyFields').loading ||
      this.props.loading;

    if (createState.error === 'EMAIL_TAKEN') {
      invalid['email'] = createState.errorMessage;
      createState.error = null;
    }
    if (createState.error === 'URLNAME_TAKEN') {
      invalid['username'] = createState.errorMessage;
      createState.error = null;
    }
    if (model.getApiState('verifyFields').error) {
      createState.errorMessage = model.getApiState('verifyFields').errorMessage;
      createState.error = model.getApiState('verifyFields').error;
    }

    let steps = this.steps();

    return (
      <form
        className='mc-modal-body mc-form mc-flex-15-col mc-auth'
        onSubmit={this.validationSubmit}
      >
        {createState.error && <MCError error={createState.errorMessage} />}
        <div className={'mc-tab-box-opacity'} style={{ overflow: 'visible' }}>
          {steps.map((step, index) => (
            <div
              key={index}
              className={`mc-tab-content ${index === activeStep ? '' : 'mc-inactive'}`}
              style={{ overflow: 'hidden' }}
            >
              <View style={{ gap: remCss(15) }}>{index === activeStep && step.content()}</View>
            </div>
          ))}
        </div>

        <div className='mc-flex-15-col mc-grow' style={{ justifyContent: 'flex-end' }}>
          <div className='mc-flex-15'>
            {activeStep > 0 && (
              <MCButton
                large
                primary
                radius
                flex
                light
                disabled={loading}
                type='button'
                onClick={() => {
                  this.scrollTo(activeStep - 1);
                }}
                iconname={BASE_ICONS.BACK_ARROW}
              />
            )}
            {steps[activeStep].fields.length > 0 && (
              <MCButton
                large
                primary
                radius
                dark
                flex
                disabled={loading}
                type='submit'
                loading={loading}
                label={
                  activeStep === steps.length
                    ? LANG_CONST.buttons.create_account
                    : LANG_CONST.buttons.next
                }
              />
            )}
          </div>
          <span className='mc-label-420' ref={(ref) => (this.submitRef = ref)}>
            {lang.t(LANG_CONST.text.already_have_account)}{' '}
            <span className='mc-primary-420 mc-pointer' onClick={this.props.onClickLogin}>
              {lang.t(LANG_CONST.text.login)}
            </span>
          </span>
        </div>
      </form>
    );
  }
}

export default Signup;
